import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Plane, Lock, User, Mail, UserCheck, XCircle } from 'lucide-react';
import { useDialog } from '../contexts/DialogContext';

const Register = () => {
  const { alert } = useDialog();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  // null = still checking with the server, true/false once we know
  const [registrationEnabled, setRegistrationEnabled] = useState(null);

  const { register, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const checkRegistrationStatus = async () => {
      try {
        const response = await fetch('/api/settings/registration-status');
        const data = await response.json();
        setRegistrationEnabled(data.registrationEnabled);
      } catch (error) {
        console.error('Failed to check registration status:', error);
        // Fail open on the client so a network hiccup doesn't just show a
        // dead end; the server enforces the real check on submit anyway.
        setRegistrationEnabled(true);
      }
    };
    checkRegistrationStatus();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, and underscores';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    // Confirm password validation
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    const result = await register({
      username: formData.username,
      email: formData.email,
      password: formData.password,
      firstName: formData.firstName,
      lastName: formData.lastName,
    });
    setIsLoading(false);

    if (result.success) {
      navigate('/login', { 
        state: { 
          message: 'Registration successful! Please check your email to verify your account.' 
        }
      });
    }
  };

  // Still waiting to hear from the server - avoid flashing the form or the
  // "closed" message before we actually know which one is correct.
  if (registrationEnabled === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  // Admin has turned registration off
  if (!registrationEnabled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 text-gray-400 rounded-full mb-4">
              <XCircle size={32} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Registration Closed</h1>
            <p className="text-gray-600 mb-6">
              New account creation is temporarily disabled. Please check back later, or contact
              us if you believe this is a mistake.
            </p>
            <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
              Return to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 text-white rounded-full mb-4">
            <Plane size={32} />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Account</h1>
          <p className="text-gray-600">Join Compflightlog to track your flights</p>
        </div>

        {/* Registration Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.firstName ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="First name"
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.lastName ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Last name"
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Username Field */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className={`block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.username ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Choose a username"
                />
              </div>
              {errors.username && (
                <p className="mt-1 text-sm text-red-600">{errors.username}</p>
              )}
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter your email"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`block w-full pl-10 pr-10 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.password ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Create a password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <UserCheck className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`block w-full pl-10 pr-10 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating account...
                </div>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          {/* Links */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
                Sign in here
              </Link>
            </p>
          </div>
        </div>

        {/* Terms */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            By creating an account, you agree to our{' '}
            <button 
              type="button"
              className="text-blue-600 hover:text-blue-500 underline bg-transparent border-none cursor-pointer"
              onClick={() => alert({ title: 'Terms of Service', message: `Last Updated: 10/26/2025

Welcome to Compflightlog (“we,” “our,” or “us”). These Terms of Service (“Terms”) govern your access to and use of our online pilot logbook platform, including our website, mobile applications, software, and any related services (collectively, the “Service”). By creating an account or using the Service, you agree to be bound by these Terms.
If you do not agree with these Terms, you may not use the Service.


1. Eligibility
You must be at least 18 years old and legally capable of entering into a binding contract to use the Service. By using the Service, you represent that you meet these requirements and that all information you provide is accurate and complete.


2. Account Registration
You must create an account to access most features of the Service. You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account. Notify us immediately if you suspect any unauthorized use of your account.


3. Use of the Service
You may use the Service only for lawful purposes and in accordance with these Terms. You agree not to:
Upload false, misleading, or fraudulent flight information.
Use the Service to violate any FAA, EASA, or other aviation authority regulations.
Attempt to access or interfere with any part of the Service, its servers, or data not intended for you.
Copy, distribute, or reverse-engineer the Service or any related software.
We reserve the right to suspend or terminate your account if you violate these Terms or use the Service in a way that could harm us or others.


4. Data Ownership and Privacy
You retain ownership of all flight data and records you input into the Service. By uploading data, you grant us a limited license to store, process, and display it solely for the purpose of operating and improving the Service.
We respect your privacy. Please review our [Privacy Policy] for information on how we collect, use, and protect your personal data.


5. Data Accuracy and Legal Use
While we strive to maintain accurate and secure records, the Service is provided for recordkeeping convenience only. It is not a legally certified record of flight experience unless printed, signed, and verified according to your aviation authority’s regulations. You are solely responsible for ensuring that all entries comply with applicable recordkeeping standards.


6. Subscriptions and Payment
Some features may require a paid subscription. By subscribing, you authorize us (or our payment processor) to charge your payment method on a recurring basis, according to your selected plan. Subscription fees are non-refundable except where required by law.
We may change pricing or features at any time, but will provide reasonable advance notice.


7. Service Availability
We strive to keep the Service available and error-free, but we do not guarantee uninterrupted operation. Scheduled maintenance, updates, or technical issues may cause temporary downtime.
We are not liable for any loss of data, income, or flight records resulting from downtime, account suspension, or other interruptions.


8. Intellectual Property
All content, software, and trademarks associated with the Service (excluding user-submitted data) are owned by us or our licensors. You may not reproduce or use our materials except as permitted by these Terms.


9. Termination
You may close your account at any time. We may suspend or terminate your account if you violate these Terms or engage in activities that harm the integrity or security of the Service. Upon termination, your license to use the Service will immediately end.
You may request a copy of your flight data before termination; we will make reasonable efforts to assist you.


10. Disclaimers and Limitation of Liability
The Service is provided “AS IS” and “AS AVAILABLE.” We make no warranties, express or implied, regarding its accuracy, reliability, or suitability for any purpose, including regulatory compliance.
To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, or consequential damages arising out of or in connection with your use of the Service.


11. Indemnification
You agree to indemnify and hold harmless Compflightlog, its officers, employees, and affiliates from any claims, losses, damages, or expenses arising from your use of the Service or violation of these Terms.


12. Changes to the Terms
We may modify these Terms from time to time. Any updates will be posted on this page with a revised “Last Updated” date. Continued use of the Service after changes are posted constitutes your acceptance of the new Terms.


13. Governing Law
These Terms are governed by and construed under the laws of [Your State/Country], without regard to its conflict of laws principles. Any disputes will be resolved in the courts of Milwaukee WI.


14. Contact
If you have any questions about these Terms, please contact us at:
Email: [contact@compflightlog.com]

© 2025 Compflightlog. All rights reserved.` })}
            >
              Terms of Service
            </button>
            {' '}and{' '}
            <button 
              type="button"
              className="text-blue-600 hover:text-blue-500 underline bg-transparent border-none cursor-pointer"
              onClick={() => alert({ title: 'Privacy Policy', message: `Last Updated: 10/26/2025

CompFlightLog (“we,” “our,” or “us”) values your privacy. This Privacy Policy explains how we collect, use, store, and protect your information when you use our website, mobile application, and related services (collectively, the “Service”).

By using CompFlightLog, you agree to this Privacy Policy. If you do not agree, please discontinue use of the Service.

---

### 1. Information We Collect

We collect the following categories of information to provide and improve our Service:

#### a. Account Information

When you register for an account, we collect your name, email address, password, and any profile information you choose to provide.

#### b. Flight Data

You may enter or import flight-related data such as flight times, aircraft details, routes, endorsements, and notes. This data is stored securely and remains your property.

#### c. Device and Usage Data

We automatically collect technical information such as:

* IP address, browser type, and operating system
* Device identifiers and app version
* Log data about your activity within the Service (e.g., pages viewed, timestamps)

#### d. Payment Information

If you purchase a subscription, our third-party payment processor (e.g., Stripe, PayPal) will handle your payment information securely. We do not store or process credit card details.

---

### 2. How We Use Your Information

We use your information to:

* Provide, operate, and maintain the CompFlightLog Service
* Sync and back up your flight data
* Respond to support requests and communicate important updates
* Improve functionality and user experience
* Enforce our Terms of Service and prevent abuse or fraud
* Comply with applicable legal requirements

We do not sell or rent your personal information.

---

### 3. Data Ownership

You retain full ownership of all flight records and data you upload. By using the Service, you grant us permission to store, process, and display your information solely for the purpose of operating and improving the Service.

You may export or delete your flight data at any time through your account settings or by contacting us.

---

### 4. Data Sharing

We may share limited data only in the following cases:

* Service Providers: With trusted vendors who help us operate CompFlightLog (e.g., hosting, analytics, payment processing).
* Legal Requirements: When required by law, regulation, or court order.
* Business Transfers: In connection with a merger, acquisition, or sale of assets, with notice provided to users.

We do not share flight or personal data with advertisers or unrelated third parties.

---

### 5. Data Storage and Security

Your data is stored on secure servers using encryption in transit (HTTPS) and at rest. We use industry-standard safeguards to protect against unauthorized access, alteration, disclosure, or destruction of your information.

However, no online service can guarantee complete security. You are responsible for maintaining the confidentiality of your account credentials.

---

### 6. Data Retention

We retain your information for as long as your account is active or as needed to provide the Service. If you delete your account, your personal and flight data will be permanently removed from our active systems within a reasonable period, except where retention is required by law.

---

### 7. Your Rights and Choices

Depending on your location, you may have the right to:

* Access, correct, or delete your personal data
* Export a copy of your flight records
* Withdraw consent for certain processing activities
* Lodge a complaint with your local data protection authority

To exercise these rights, contact us at [privacy@compflightlog.com](mailto:privacy@compflightlog.com).

---

### 8. Cookies and Analytics

CompFlightLog uses cookies and similar technologies to enable site functionality and analyze usage trends. You can adjust cookie preferences in your browser settings. Analytics data is aggregated and does not personally identify you.

---

### 9. International Users

If you access CompFlightLog from outside the United States, please note that your information may be transferred to and processed in the U.S. or other countries with different data protection laws.

---

### 10. Children’s Privacy

CompFlightLog is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from minors. If we discover that such information has been provided, we will delete it promptly.

---

### 11. Changes to This Policy

We may update this Privacy Policy from time to time. When we make material changes, we will notify you by email or post a notice within the Service. The updated policy will take effect on the date listed above.

---

### 12. Contact Us

If you have any questions about this Privacy Policy or our data practices, please contact:

CompFlightLog
Email: [privacy@compflightlog.com](mailto:privacy@compflightlog.com)

---

© 2025 CompFlightLog. All rights reserved.
` })}
            >
              Privacy Policy
            </button>
          </p>
        </div>  
      </div>
    </div>
  );
};

export default Register;
